import React, { useEffect, useState, useRef } from 'react';
import { Stage, Layer, Image } from 'react-konva';
import useImage from 'use-image';
import { DrawAnnotationsProps, Polygon, PointProps } from '../types';
import DraggablePoint from './DraggablePoint';
import DraggablePolygon from './DraggablePolygon';
import { pointsToFlatArray, updatePointCoordinates, moveAllPoints } from '../utils/coordinateUtils';

const DrawAnnotations: React.FC<DrawAnnotationsProps> = ({
  polygons,
  activePolygonIndex,
  onPolygonChange,
  onAddPolygon,
  onSelectPolygon,
  canvasWidth,
  canvasHeight,
  imageBackground = null,
}) => {
  const [image] = useImage(imageBackground || '');
  const [isDraggingPolygon, setIsDraggingPolygon] = useState(false);
  const [lastPointerPosition, setLastPointerPosition] = useState<{ x: number, y: number } | null>(null);
  const stageRef = useRef(null);

  // Get the active polygon - could be undefined if activePolygonIndex is invalid
  const activePolygon = activePolygonIndex >= 0 && activePolygonIndex < polygons.length
    ? polygons[activePolygonIndex]
    : undefined;

  const activePoints = activePolygon ? activePolygon.points : [];

  const handlePointDragMove = (id: string, x: number, y: number) => {
    if (!activePolygon) return;

    const updatedPolygons = [...polygons];
    const newPoints = updatePointCoordinates(activePoints, id, x, y);
    updatedPolygons[activePolygonIndex] = {
      ...activePolygon,
      points: newPoints,
    };
    onPolygonChange(updatedPolygons);
  };

  const handlePolygonDragStart = (e: any) => {
    if (!activePolygon) return;

    setIsDraggingPolygon(true);
    const pointerPos = e.target.getStage().getPointerPosition();
    setLastPointerPosition(pointerPos);
    e.evt.preventDefault();
  };

  const handlePolygonDragMove = (e: any) => {
    if (!isDraggingPolygon || !lastPointerPosition || !activePolygon) return;

    const pointerPos = e.target.getStage().getPointerPosition();
    const dx = pointerPos.x - lastPointerPosition.x;
    const dy = pointerPos.y - lastPointerPosition.y;

    const updatedPolygons = [...polygons];
    const newPoints = moveAllPoints(activePoints, dx, dy);

    updatedPolygons[activePolygonIndex] = {
      ...activePolygon,
      points: newPoints,
    };

    setLastPointerPosition(pointerPos);
    onPolygonChange(updatedPolygons);
    e.evt.preventDefault();
  };

  const handlePolygonDragEnd = () => {
    setIsDraggingPolygon(false);
    setLastPointerPosition(null);
  };

  const handlePolygonSelect = (polygonId: string) => {
    const index = polygons.findIndex((poly) => poly.id === polygonId);
    if (index !== -1) {
      onSelectPolygon(index);
    }
  };

  // Handle clicks on the canvas background to deselect
  const handleCanvasClick = (e: any) => {
    // Only handle clicks on the stage itself, not on its children (polygons, points)
    if (e.target === e.currentTarget) {
      onSelectPolygon(-1); // -1 indicates no selection
    }
  };

  // Add a document-wide click handler to deselect when clicking outside the canvas
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      // Skip if clicking inside the stage
      if (stageRef.current && (stageRef.current as any).container().contains(e.target)) {
        return;
      }

      // Check if clicked on a button or form element
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT' ||
        target.closest('button') ||
        target.closest('input') ||
        target.closest('.polygon-manager') ||
        target.closest('.areas-sidebar') ||
        target.closest('.react-datepicker') ||
        target.closest('.react-datepicker-wrapper') ||
        target.closest('.react-datepicker-popper') ||
        target.closest('.datapicker-container') ||
        target.closest('.datepicker-action')
      ) {
        return;
      }

      // Otherwise deselect
      onSelectPolygon(-1);
    };

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [onSelectPolygon]);

  // Convert canvas container to absolute position for proper placement
  const containerStyle = {
    position: 'relative' as const,
    width: `${canvasWidth}px`,
    height: `${canvasHeight}px`,
  };

  return (
    <div style={containerStyle}>
      <Stage
        ref={stageRef}
        width={canvasWidth}
        height={canvasHeight}
        onClick={handleCanvasClick}
        className="border border-gray-200 rounded"
      >
        <Layer>
          {image && <Image image={image} width={canvasWidth} height={canvasHeight} opacity={0.5} />}

          {polygons.map((polygon) => {
            const points = pointsToFlatArray(polygon.points);
            return (
              <DraggablePolygon
                key={polygon.id}
                id={polygon.id}
                points={points}
                onDragStart={handlePolygonDragStart}
                onDragMove={handlePolygonDragMove}
                onDragEnd={handlePolygonDragEnd}
                isSelected={activePolygon ? polygon.id === activePolygon.id : false}
                onSelect={handlePolygonSelect}
              />
            );
          })}

          {activePolygon && activePolygon.points.map((point) => (
            <DraggablePoint
              key={point.id}
              id={point.id}
              x={point.x}
              y={point.y}
              onDragMove={handlePointDragMove}
              onDragEnd={handlePolygonDragEnd}
              isSelected
            />
          ))}
        </Layer>
      </Stage>
    </div>
  );
};

export default DrawAnnotations;
